/**
 * Transport Fee Management Component
 * Displays transport route details and fee plan with installment information
 */
import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import LoadingComponent from '../../loadingComponent';
import { generateUnAssignFeeURL } from '../../../utils/unAssignFeeNavigation';

/**
 * Transport Fee Management Component
 * @param {Object} studentData - Student data object
 * @param {boolean} isLoading - Loading state
 * @param {Object} toast - Toast reference for notifications
 * @param {Function} navigate - React Router navigate function
 * @param {Array} feeTermsRawData - Raw fee terms data from parent (fetched once on Finance menu click)
 * @param {boolean} feetypeWiseFeeLoading - Loading state for fee terms data
 * @returns {JSX.Element} - Transport Fee Management component
 */
export const TransportFeeManagement = ({ 
    studentData, 
    isLoading: parentLoading, 
    toast, 
    navigate, 
    feeTermsRawData = null, 
    feetypeWiseFeeLoading = false 
}) => {
    const [feeplanData, setFeeplanData] = useState([]);

    useEffect(() => {
        if (feeTermsRawData && feeTermsRawData.length > 0) {
            processTransportFeeData(feeTermsRawData);
        } else {
            setFeeplanData([]);
        }
    }, [feeTermsRawData]);

    const processTransportFeeData = (rawData) => {
        try {
            // Filter for transport fee types only
            const transportFees = rawData.filter(item => item.isTransportFeeType === true);
            
            // Sort by term (1-10)
            const sortedFees = transportFees.sort((a, b) => (a.term || 0) - (b.term || 0));

            // Transform to display format
            const transformedData = sortedFees.map(item => ({
                installmentName: item.paymentTerm || 'N/A',
                term: item.term,
                feeType: item.feeType || 'N/A',
                dueDate: item.dueDate,
                actualAmount: item.termWiseActualValue || 0,
                concession: item.concession || 0,
                netAmount: (item.termWiseActualValue || 0) - (item.concession || 0),
                paidAmount: item.amountPaid || 0,
                dueAmount: item.dueAmount || 0
            }));

            setFeeplanData(transformedData);
        } catch (err) {
            console.error('Error processing transport fee data:', err);
            setFeeplanData([]);
        }
    };

    // Sample route data (keeping this static for now as it's not in the API response)
    const routeData = {
        feeType: 'Transport Fee',
        isServiceBased: 'No',
        isProRata: 'No',
        routeDetails: [
            { month: 'Jun-2025', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Jul-2025', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Aug-2025', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Sep-2025', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Oct-2025', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Nov-2025', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Dec-2025', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Jan-2026', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Feb-2026', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' },
            { month: 'Mar-2026', pickupPlace: '7 - ALKAPURTOWNSHIP', dropPlace: 'FLOATILLA APPARTSEMENTS' }
        ]
    };

    if (feetypeWiseFeeLoading || parentLoading) {
        return <LoadingComponent />;
    }

    // Column body templates for route details
    const monthBodyTemplate = (rowData) => {
        return <span className="month-cell">{rowData.month}</span>;
    };

    const pickupPlaceBodyTemplate = (rowData) => {
        return (
            <span className="link-cell">
                {rowData.pickupPlace}
            </span>
        );
    };

    const dropPlaceBodyTemplate = (rowData) => {
        return (
            <span className="link-cell">
                {rowData.dropPlace}
            </span>
        );
    };

    // Column body templates for feeplan details
    const installmentBodyTemplate = (rowData) => {
        // Format due date if available
        const formatDueDate = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            const options = { month: 'short', year: 'numeric' };
            return date.toLocaleDateString('en-US', options);
        };

        return (
            <div>
                <div className="installment-name">
                    {rowData.installmentName}
                </div>
                {rowData.dueDate && (
                    <div className="installment-due-date">
                        Due: {formatDueDate(rowData.dueDate)}
                    </div>
                )}
            </div>
        );
    };

    const amountBodyTemplate = (value) => {
        return <span className="amount-cell">{value.toFixed(2)}</span>;
    };

    const dueAmountBodyTemplate = (rowData) => {
        const isDue = rowData.dueAmount > 0;
        return (
            <span className="due-amount-cell" style={{ 
                color: isDue ? '#DC2626' : '#059669'
            }}>
                {isDue ? rowData.dueAmount.toFixed(2) : '-'}
            </span>
        );
    };

    // Handle Unassign button click
    const handleUnassignClick = () => {
        if (navigate && studentData) {
            const orgId = localStorage.getItem('selectedOrgId') || localStorage.getItem('orgId') || '';
            const url = generateUnAssignFeeURL({
                studentId: studentData.userId,
                searchLevel: 2,
                orgId: orgId
            });
            
            // Update browser URL to maintain student profile context
            try {
                const profileUrl = `/studentProfile?studentId=${encodeURIComponent(studentData.userId)}`;
                window.history.pushState({}, '', profileUrl);
            } catch (e) {
                console.error('Error updating URL:', e);
            }
            
            navigate(url);
        }
    };

    return (
        <div className="transport-fee-management-container">
            {/* Route Details Section */}
            <div className="route-details-section">
                <div className="section-header">
                    <h3 className="section-title">
                        Route Details
                    </h3>
                    <div className="action-buttons">
                        <Button 
                            label="Unassign" 
                            className="p-button-outlined p-button-danger"
                            onClick={handleUnassignClick}
                            disabled={!navigate || !studentData?.userId}
                            tooltip={!navigate || !studentData?.userId ? "Navigation not available" : "Unassign transport fee"}
                        />
                    </div>
                </div>

                <div className="route-info-cards">
                    <div className="info-card">
                        <span className="info-label">Fee Type:</span>
                        <span className="info-value">{routeData.feeType}</span>
                    </div>
                    <div className="info-card">
                        <span className="info-label">Is Service Based:</span>
                        <span className="info-value">{routeData.isServiceBased}</span>
                    </div>
                    <div className="info-card">
                        <span className="info-label">Is Pro-Rata:</span>
                        <span className="info-value">{routeData.isProRata}</span>
                    </div>
                </div>

                {/* <div className="route-table-wrapper">
                    <div className="route-table-header">
                        <h4 className="section-title" style={{ fontSize: '0.9375rem' }}>
                            Route Type
                        </h4>
                    </div>
                    <div className="route-table-container">
                        <DataTable
                            value={routeData.routeDetails}
                            className="route-details-table"
                            showGridlines
                            stripedRows
                        >
                            <Column 
                                field="month" 
                                header="Month" 
                                body={monthBodyTemplate}
                                headerStyle={{ width: '150px' }}
                                style={{ width: '150px' }}
                            />
                            <Column 
                                field="pickupPlace" 
                                header="Pickup Place" 
                                body={pickupPlaceBodyTemplate}
                                headerStyle={{ minWidth: '300px' }}
                                style={{ minWidth: '300px' }}
                            />
                            <Column 
                                field="dropPlace" 
                                header="Drop Place" 
                                body={dropPlaceBodyTemplate}
                                headerStyle={{ minWidth: '300px' }}
                                style={{ minWidth: '300px' }}
                            />
                        </DataTable>
                    </div>
                </div> */}
            </div>

            <Divider style={{ margin: '2rem 0' }} />

            {/* Feeplan Details Section */}
            <div className="feeplan-details-section">
                <h3 className="section-title" style={{ marginBottom: '1.5rem' }}>
                    Feeplan Details
                </h3>

                <div className="feeplan-table-container">
                    <DataTable
                        value={feeplanData}
                        className="feeplan-details-table"
                        showGridlines
                        stripedRows
                        emptyMessage="No transport fee data available"
                    >
                        <Column 
                            header="Type" 
                            body={installmentBodyTemplate}
                            headerStyle={{ width: '200px' }}
                            style={{ width: '200px' }}
                        />
                        <Column 
                            field="actualAmount" 
                            header="Actual Amount"
                            body={(rowData) => amountBodyTemplate(rowData.actualAmount)}
                            headerStyle={{ width: '140px' }}
                            style={{ width: '140px' }}
                        />
                        <Column 
                            field="concession" 
                            header="Concession"
                            body={(rowData) => amountBodyTemplate(rowData.concession)}
                            headerStyle={{ width: '140px' }}
                            style={{ width: '140px' }}
                        />
                        <Column 
                            field="netAmount" 
                            header="Net Amount"
                            body={(rowData) => amountBodyTemplate(rowData.netAmount)}
                            headerStyle={{ width: '140px' }}
                            style={{ width: '140px' }}
                        />
                        <Column 
                            field="paidAmount" 
                            header="Paid Amount"
                            body={(rowData) => amountBodyTemplate(rowData.paidAmount)}
                            headerStyle={{ width: '140px' }}
                            style={{ width: '140px' }}
                        />
                        <Column 
                            field="dueAmount" 
                            header="Due Amount"
                            body={dueAmountBodyTemplate}
                            headerStyle={{ width: '140px' }}
                            style={{ width: '140px' }}
                        />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default TransportFeeManagement;
